<template>
    <div v-if="categories && categories.length > 0" :class="['pb-4 lg:pb-0', { 'lg:mb-6': isOpenState }]">
        <button
            @click="isOpenState = !isOpenState"
            class="mb-6 flex w-full cursor-pointer items-center justify-between border-b-2 border-[rgb(212_215_202)] px-4 pb-3">
            <span class="font-monument-extended block text-[14px] leading-[33px]">{{ label }}</span>
            <IconChevronDown
                :class="{ '!-rotate-180': isOpenState }"
                class="size-3 -rotate-90 transition-all lg:-rotate-0" />
        </button>
        <div
            class="overflow-hidden pt-1.5 lg:px-4 lg:duration-300"
            :class="{ 'filter-open': isOpenState, 'filter-closed': !isOpenState }"
            :aria-hidden="!isOpenState"
            role="group">
            <div class="flex h-full flex-col">
                <div class="flex-1 overflow-y-auto">
                    <div class="mt-3 lg:hidden">
                        <button type="button" class="mb-8 flex items-center leading-none" @click="isOpenState = false">
                            <ArrowLeftIcon class="mr-3 size-5 stroke-2" />
                            Terug
                        </button>
                        <span class="styled-as-h3 mb-5 block text-xl">{{ label }}</span>
                    </div>
                    <div class="mb-10 grid grid-cols-1 gap-x-16 lg:mb-0">
                        <!-- Sale/Nieuw page: show only subcategories + back link -->
                        <template v-if="isOnSpecialPage && specialCategoryChildren.length > 0">
                            <!-- <div class="mb-4">
                                <CategoryLink
                                    :to="parentCategoryUrl"
                                    @click="mobileFiltersMenuStore.closeMenu()"
                                    class="mb-0 !flex cursor-pointer items-center text-sm font-normal">
                                    <ArrowLeftIcon class="mr-2 size-4 stroke-2" />
                                    <span class="flex-1">Terug naar alles</span>
                                </CategoryLink>
                            </div> -->

                            <template v-for="category in specialCategoryChildren" :key="category.id">
                                <CategoryTreeItem :category="category" :current-category-id="currentCategoryId" />
                            </template>
                        </template>

                        <!-- Regular page: standard category tree -->
                        <template v-else>
                            <div class="mb-4">
                                <!-- SALE category at top with sale-tag (hidden on sale page) -->
                                <div v-if="saleCategory && !isOnSalePage && isInTweakwiseTree(saleCategory)" class="mb-4">
                                    <CategoryLink
                                        :category-id="saleCategory.id"
                                        :to="'/' + saleCategory.seoUrls?.[0]?.seoPathInfo"
                                        @click="
                                            () => {
                                                mobileFiltersMenuStore.closeMenu();
                                                onCategoryClick(saleCategory!.id);
                                            }
                                        "
                                        class="mb-0 !flex cursor-pointer items-center text-sm font-normal">
                                        <span class="sale-tag !text-xs">{{ saleCategory.translated.name }}</span>
                                    </CategoryLink>
                                </div>

                                <!-- Nieuw category (hidden on nieuw page) -->
                                <div v-if="nieuwCategory && !isOnNieuwPage && isInTweakwiseTree(nieuwCategory)" class="mb-4">
                                    <CategoryLink
                                        :category-id="nieuwCategory.id"
                                        :to="'/' + nieuwCategory.seoUrls?.[0]?.seoPathInfo"
                                        @click="
                                            () => {
                                                mobileFiltersMenuStore.closeMenu();
                                                onCategoryClick(nieuwCategory!.id);
                                            }
                                        "
                                        class="mb-0 !flex cursor-pointer items-center text-sm font-normal">
                                        <span class="flex-1">{{ nieuwCategory.translated.name }}</span>
                                    </CategoryLink>
                                </div>
                            </div>

                            <!-- Regular categories (excluding Nieuw and SALE) -->
                            <template v-for="category in regularCategories" :key="category.id" class="mb-4 last:mb-0">
                                <CategoryTreeItem :category="category" :current-category-id="currentCategoryId" />
                            </template>
                        </template>
                    </div>
                </div>
                <div class="sticky right-0 bottom-0 left-0 bg-white pb-5 lg:hidden">
                    <button
                        type="button"
                        class="btn w-full bg-black text-white"
                        @click="
                            isOpenState = false;
                            mobileFiltersMenuStore.closeMenu();
                        ">
                        {{ $snippets['headless.plp.view'] }} {{ total }} {{ $snippets['headless.plp.items'] }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import CategoryTreeItem from './CategoryTreeItem.vue';
    import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
    import { isSpecialCategoryName } from '~/utils/plp';

    interface Category {
        id: string;
        parentId?: string;
        active?: boolean;
        translated: {
            name: string;
        };
        seoUrls: Array<{ seoPathInfo: string }>;
        children?: Category[];
    }

    const props = withDefaults(
        defineProps<{
            label?: string;
            categories: Category[];
            currentCategoryId?: string;
            isOpen?: boolean;
            total?: number;
        }>(),
        {
            label: 'Categorie',
            isOpen: true,
        },
    );

    const keepFilters = useKeepFilters();
    const tweakwiseCategoryNames = inject<Ref<Set<string> | null>>('tweakwiseCategoryNames', ref(null));
    const route = useRoute();
    const mobileFiltersMenuStore = useMobileFiltersMenuStore();

    const { apiClient } = useShopwareContext();
    function onCategoryClick(categoryId: string) {
        if (!keepFilters.value || Object.keys(route.query).length === 0) prefetchPlpListing(apiClient, categoryId);
    }

    const tweakwiseSelectedSubtreeNames = inject<Ref<Set<string> | null>>('tweakwiseSelectedSubtreeNames', ref(null));
    function isInTweakwiseTree(cat: Category): boolean {
        return keepFilters.value || !tweakwiseCategoryNames.value || tweakwiseCategoryNames.value.has(cat.translated?.name?.toLowerCase());
    }
    function isInTweakwiseSubtree(cat: Category): boolean {
        return keepFilters.value || (!tweakwiseSelectedSubtreeNames.value ? isInTweakwiseTree(cat) : tweakwiseSelectedSubtreeNames.value.has(cat.translated?.name?.toLowerCase()));
    }

    const isOpenState = ref(props.isOpen);

    const isOnSalePage = computed(() => route.path.includes('/sale'));
    const isOnNieuwPage = computed(() => route.path.includes('/nieuw'));

    const saleCategory = computed(() => {
        return props.categories.find(cat => cat.translated?.name?.toLowerCase() === 'sale');
    });

    const nieuwCategory = computed(() => {
        return props.categories.find(
            cat => cat.translated?.name?.toLowerCase() === 'nieuw' || cat.translated?.name?.toLowerCase() === 'new',
        );
    });

    const regularCategories = computed(() => {
        return props.categories.filter(cat => {
            const name = cat.translated?.name?.toLowerCase();
            return !isSpecialCategoryName(name) && isInTweakwiseTree(cat);
        });
    });

    // Detect if we're on a special page (sale/nieuw) and resolve its subcategories
    const isOnSpecialPage = computed(() => isOnSalePage.value || isOnNieuwPage.value);

    const specialCategoryChildren = computed(() => {
        if (!isOnSpecialPage.value) return [];

        const specialCat = isOnSalePage.value ? saleCategory.value : nieuwCategory.value;

        let children: Category[] = [];
        if (specialCat?.children?.length) {
            children = specialCat.children.filter(child => child.active !== false);
        }

        return children.filter(child => {
            const name = child.translated?.name?.toLowerCase();
            if (isSpecialCategoryName(name)) return false;
            return isInTweakwiseSubtree(child);
        });
    });

    const parentCategoryUrl = computed(() => {
        if (!isOnSpecialPage.value) return '';
        const segment = isOnSalePage.value ? '/sale' : '/nieuw';
        const idx = route.path.indexOf(segment);
        return idx !== -1 ? route.path.substring(0, idx) + '/' : '/';
    });
</script>
